import { useFirebaseEmulators, isProductionEnv } from '../../config/env';
import { normalizeError, isAppError } from '../../core/errors/index';
import { describeErrorSource, isMeaningfulStackTrace } from '../../core/errors/describeError';
import { logAuditEvent } from '../audit/AuditService';
import { getTraceContext, newSpanId } from '../audit/traceContext';

type CrashContext = Record<string, unknown> & {
  uid?: string;
  role?: string;
  houseId?: string;
};

const buffer: Record<string, unknown>[] = [];

function resolveCrashStack(error: unknown, appErr: ReturnType<typeof normalizeError>): string | undefined {
  const source = describeErrorSource(error);
  const fromDetails = typeof appErr.details.sourceStack === 'string'
    ? appErr.details.sourceStack
    : '';
  const stack = fromDetails || source.sourceStack || '';
  return isMeaningfulStackTrace(stack) ? stack.trim() : undefined;
}

/**
 * Web crash reporting bridge — persists structured errors to the audit trail.
 */
export function reportError(error: unknown, context: CrashContext = {}) {
  const appErr = isAppError(error) ? error : normalizeError(error);
  const { traceId, requestId } = getTraceContext();
  const stack = resolveCrashStack(error, appErr);

  const entry = {
    code: appErr.code,
    category: appErr.category,
    userMessageKey: appErr.userMessageKey,
    message: appErr.message,
    traceId: appErr.traceId ?? traceId,
    requestId,
    spanId: appErr.spanId ?? newSpanId('crash'),
    details: appErr.details,
    stack,
    context,
    ts: new Date().toISOString(),
    env: isProductionEnv() ? 'production' : 'local',
  };

  buffer.push(entry);
  if (buffer.length > 50) buffer.shift();

  if (!shouldSkipCrashAudit(appErr)) {
    logAuditEvent({
      action: 'error.crash',
      severity: 'error',
      spanId: entry.spanId,
      payload: {
        code: entry.code,
        category: entry.category,
        userMessageKey: entry.userMessageKey,
        developerMessage: entry.message,
        traceId: entry.traceId,
        requestId: entry.requestId,
        details: entry.details,
        ...context,
        stack,
      },
      resource: { type: 'error', id: appErr.code, path: '' },
      actor: {
        uid: context.uid ?? 'anonymous',
        role: context.role ?? 'unknown',
        houseId: context.houseId ?? '',
      },
    }).catch(() => {});
  }

  if (useFirebaseEmulators() || !isProductionEnv()) {
    console.error('[CrashReport:emulator]', entry);
    return;
  }

  console.error('[CrashReport]', entry);
}

function shouldSkipCrashAudit(appErr: ReturnType<typeof normalizeError>): boolean {
  if (appErr.category === 'offline' || appErr.category === 'unavailable') return true;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  const msg = String(appErr.message ?? '').toLowerCase();
  return /failed to fetch|networkerror|load failed|chunkloaderror|importing a module script failed/i.test(msg);
}

export function getRecentCrashReports(): Record<string, unknown>[] {
  return [...buffer];
}

export function initCrashReporting() {
  if (typeof window === 'undefined') return;

  window.addEventListener('error', (event) => {
    reportError(event.error ?? new Error(event.message), { source: 'window.onerror' });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const err = event.reason instanceof Error
      ? event.reason
      : new Error(String(event.reason));
    reportError(err, { source: 'unhandledrejection' });
  });

  return { enabled: true };
}
